import { c as _c } from "react/compiler-runtime";
import type { OrgArtifact } from '@onefootprint/request-types/dashboard';
import { useState } from 'react';
import { decodeBase64Utf8 } from '../utils/decode-base64';
import { collectCitedSourceLabels } from '../utils/source-citations';
import useSources, { type CitationPair } from './use-sources';
type UseSourcesDialogArgs = {
  investigationId: string;
  selectedArtifact: OrgArtifact | null;
  selectedArtifactContent?: string;
  editingMarkdownContent?: string;
};
const getSelectedMarkdownContent = ({
  selectedArtifact,
  selectedArtifactContent,
  editingMarkdownContent
}: UseSourcesDialogArgs) => {
  if (selectedArtifact?.mimeType !== 'text/markdown') return '';
  if (editingMarkdownContent !== undefined) return editingMarkdownContent;
  return selectedArtifactContent ? decodeBase64Utf8(selectedArtifactContent) ?? '' : '';
};
const sameCitationPairs = (a: CitationPair[], b: CitationPair[]) => a.length === b.length && a.every((pair, i) => pair.sourceLabel === b[i].sourceLabel && pair.locator === b[i].locator);
const useSourcesDialog = args => {
  const $ = _c(16);
  const [isOpen, setIsOpen] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [citationPairs, setCitationPairs] = useState(t0);
  const {
    data
  } = useSources(args.investigationId, citationPairs);
  let citedSourceLabels;
  let sources;
  let t1;
  if ($[1] !== args || $[2] !== data?.sources) {
    sources = data?.sources ?? [];
    const selectedMarkdownContent = getSelectedMarkdownContent(args);
    citedSourceLabels = collectCitedSourceLabels(selectedMarkdownContent);
    t1 = sources.filter(source => citedSourceLabels.has(source.label));
    $[1] = args;
    $[2] = data?.sources;
    $[3] = citedSourceLabels;
    $[4] = sources;
    $[5] = t1;
  } else {
    citedSourceLabels = $[3];
    sources = $[4];
    t1 = $[5];
  }
  const citedSourceCount = t1.length;
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = pairs => {
      setCitationPairs(prev => sameCitationPairs(prev, pairs) ? prev : pairs);
    };
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleCitationPairsChange = t2;
  let t3;
  if ($[7] !== sources.length) {
    t3 = sources.length > 0 ? () => setIsOpen(true) : undefined;
    $[7] = sources.length;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const open = t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setIsOpen(false);
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== citedSourceCount || $[11] !== citedSourceLabels || $[12] !== isOpen || $[13] !== open || $[14] !== sources) {
    t5 = {
      citedSourceCount,
      citedSourceLabels,
      close: t4,
      isOpen,
      onCitationPairsChange: handleCitationPairsChange,
      open,
      sources
    };
    $[10] = citedSourceCount;
    $[11] = citedSourceLabels;
    $[12] = isOpen;
    $[13] = open;
    $[14] = sources;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};
export default useSourcesDialog;